import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const STATUS_MESSAGES = {
  missing_token: "Verification link is missing or invalid.",
  session_not_found: "This verification link is invalid or has expired.",
  expired: "This verification code has expired. Please request a new code.",
  server_error: "Something went wrong. Please try again later.",
};

const VERIFY_MESSAGES = {
  invalid_code: "That code is not correct. Please check the email and try again.",
  too_many_attempts: "Too many attempts. Please request a new code.",
  expired: "This verification code has expired. Please request a new code.",
  session_not_found: "This verification link is invalid or has expired.",
  server_error: "Something went wrong. Please try again later.",
};

function getStatusError(reason) {
  return STATUS_MESSAGES[reason] || STATUS_MESSAGES.server_error;
}

function getVerifyError(reason) {
  return VERIFY_MESSAGES[reason] || VERIFY_MESSAGES.server_error;
}

function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [pageState, setPageState] = useState("loading");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [marketingEmailConsent, setMarketingEmailConsent] = useState(false);
  const [successTitle, setSuccessTitle] = useState("Email verified");
  const [successText, setSuccessText] = useState(
    "Your Articulate account email has been verified. You may return to WhatsApp."
  );

  const codeIsValid = code.length === 6;

  useEffect(() => {
    let cancelled = false;

    async function loadStatus() {
      setMessage("");
      setError("");

      if (!token) {
        setPageState("error");
        setError(STATUS_MESSAGES.missing_token);
        return;
      }

      setPageState("loading");

      try {
        const response = await fetch(
          `/api/account/email-verification/status?token=${encodeURIComponent(token)}`
        );
        const data = await response.json().catch(() => ({}));

        if (cancelled) {
          return;
        }

        if (!data.success) {
          setPageState("error");
          setError(getStatusError(data.reason));
          return;
        }

        setMaskedEmail(data.masked_email || "");

        if (data.email_verified || data.status === "verified") {
          setSuccessTitle("Email already verified");
          setSuccessText("You may return to WhatsApp.");
          setPageState("success");
          return;
        }

        setPageState("pending");
      } catch (err) {
        if (!cancelled) {
          setPageState("error");
          setError(STATUS_MESSAGES.server_error);
        }
      }
    }

    loadStatus();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleCodeChange = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(digitsOnly);
    setError("");
    setMessage("");
  };

  const handleVerify = async () => {
    if (!token || !codeIsValid || verifying) {
      return;
    }

    setVerifying(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/account/email-verification/verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          code,
          marketing_email_consent: marketingEmailConsent,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (data.success && data.email_verified) {
        setMaskedEmail(data.masked_email || maskedEmail);
        setSuccessTitle("Email verified");
        setSuccessText(
          "Your Articulate account email has been verified. You may return to WhatsApp."
        );
        setPageState("success");
        return;
      }

      setError(getVerifyError(data.reason));
    } catch (err) {
      setError(VERIFY_MESSAGES.server_error);
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!token || resending) {
      return;
    }

    setResending(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/account/email-verification/resend", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });
      const data = await response.json().catch(() => ({}));

      if (data.success && data.status === "already_verified") {
        setSuccessTitle("Email already verified");
        setSuccessText("You may return to WhatsApp.");
        setPageState("success");
        return;
      }

      if (data.success && data.resent) {
        setMaskedEmail(data.masked_email || maskedEmail);
        setCode("");
        setMessage("A new verification code has been sent.");
        return;
      }

      const reason = data.reason || "server_error";
      if (reason === "expired" || reason === "session_not_found") {
        setPageState("error");
        setError(STATUS_MESSAGES.session_not_found);
        return;
      }

      setError(STATUS_MESSAGES.server_error);
    } catch (err) {
      setError(STATUS_MESSAGES.server_error);
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (codeIsValid) {
      handleVerify();
    }
  };

  return (
    <main className="verify-email-page">
      <section className="verify-email-card" aria-live="polite">
        <img
          className="verify-email-logo"
          src="https://chatbotmia.blob.core.windows.net/assets/Articulate%20export_Logo%20-%20Full%20Color_NoBG_trimmed.PNG"
          alt="Articulate"
        />

        {pageState === "loading" ? (
          <p className="verify-email-muted">Checking verification link...</p>
        ) : null}

        {pageState === "error" ? (
          <>
            <h1>Verification unavailable</h1>
            <p className="verify-email-error">{error}</p>
          </>
        ) : null}

        {pageState === "success" ? (
          <>
            <h1>{successTitle}</h1>
            <p>{successText}</p>
          </>
        ) : null}

        {pageState === "pending" ? (
          <>
            <h1>Verify your email</h1>
            <p>
              We sent a verification code to{" "}
              <strong>{maskedEmail || "your email address"}</strong>.
            </p>

            <form className="verify-email-form" onSubmit={handleSubmit}>
              <label htmlFor="email-verification-code">6-digit code</label>
              <input
                id="email-verification-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                value={code}
                onChange={handleCodeChange}
                maxLength={6}
                placeholder="123456"
                disabled={verifying}
              />

              <label className="verify-email-checkbox">
                <input
                  type="checkbox"
                  checked={marketingEmailConsent}
                  onChange={(event) =>
                    setMarketingEmailConsent(event.target.checked)
                  }
                  disabled={verifying}
                />
                <span>
                  I&apos;d like to receive updates, learning tips, and offers from
                  Articulate by email. I can unsubscribe anytime.
                </span>
              </label>

              {error ? <div className="verify-email-error">{error}</div> : null}
              {message ? <div className="verify-email-success">{message}</div> : null}

              <button
                type="submit"
                className="verify-email-primary"
                disabled={!codeIsValid || verifying}
              >
                {verifying ? "Verifying..." : "Verify email"}
              </button>

              <button
                type="button"
                className="verify-email-secondary"
                onClick={handleResend}
                disabled={resending || verifying}
              >
                {resending ? "Sending..." : "Resend code"}
              </button>
            </form>

            <p className="verify-email-muted">
              You can return to WhatsApp after your email is verified.
            </p>
          </>
        ) : null}
      </section>
    </main>
  );
}

export default VerifyEmailPage;
